// Adds the local guest cart (guest_cart.js) to the account's cart once the visitor has
// logged in. Runs on every page that loads this bundle while a guest cart is stored, so
// the first page after login does it. The server replays the cart through the cart rules
// and reports every line (merge_guest_cart in www/subscription.py).
//
// The guest cart is cleared only when the server answers ok. A refusal, a server error or
// a lost request leaves it stored, and the next page opened tries again.

frappe.provide("ioi_partners")

const MERGED_EVENT = "ioi_partners:guest_cart_merged"
const CART_PAGE = "/subscription"
// sessionStorage keys. A failed merge is explained once per browser session, not on every
// page that tries again. A merge report made off the cart page waits for it (show_result).
const NOTICE_SHOWN_KEY = "ioi_partners.guest_cart_merge_notice"
const REPORT_KEY = "ioi_partners.guest_cart_merge_report"
// Merged lines listed in the notice shown off the cart page; the report has them all.
const NOTICE_LINES = 3
const NOTICE_SECONDS = 15

let merging = false
// True while the merge report dialog (show_report) is open.
let report_open = false

function merge() {
	const items = ioi_partners.guest_cart.items()
	if (merging || ioi_partners.is_guest() || !items.length) {
		return
	}

	merging = true
	frappe.call({
		method: "ioi_partners.www.subscription.merge_guest_cart",
		args: {
			items: items,
			merge_id: ioi_partners.guest_cart.merge_id(),
		},
		callback: (r) => {
			const result = r && r.message
			if (!result || !result.ok) {
				show_failure(result && result.reason, result && result.message)
				return
			}

			ioi_partners.guest_cart.clear()
			window.dispatchEvent(new CustomEvent(MERGED_EVENT, { detail: result }))
			// A replayed result was already reported, by another tab or an earlier request.
			if (!result.replayed) {
				show_result(result)
			}
		},
		error: () => show_failure(),
		always: () => {
			merging = false
		},
	})
}

function show_failure(reason, server_message) {
	if (notice_already_shown(reason || "error")) {
		return
	}

	// States what happened only. A new account can have no reseller yet (I CHOOSE LATER),
	// and there is no support address to send the visitor to.
	let message = reason === "no_contact"
		? __("Your account is not linked to a company yet, so the products you selected before logging in could not be added to your cart. Your selection is kept on this device.")
		// Reseller mode (focus.py), with a focus ("focus": the selection is not the customer's) or
		// without one ("no_focus": the reseller's own company has no cart in the e-shop).
		: reason === "focus" || reason === "no_focus"
		? __("In reseller mode, the products you selected before logging in are not added to a cart. Your selection is kept on this device.")
		: __("The products you selected before logging in could not be added to your cart. Your selection is kept on this device, and it will be tried again on the next page you open.")
	if (server_message) {
		message += `<br><br>${frappe.utils.escape_html(server_message)}`
	}

	frappe.msgprint({
		title: __("Your selection could not be added to your cart"),
		message: message,
		indicator: "orange",
	})
}

function notice_already_shown(reason) {
	const key = `${NOTICE_SHOWN_KEY}.${reason}`
	try {
		if (sessionStorage.getItem(key)) {
			return true
		}
		sessionStorage.setItem(key, "1")
	} catch (e) {
		// Storage blocked: the notice is shown on every page rather than never.
	}
	return false
}

// On the cart page the full report opens over the cart it describes. Anywhere else - the
// catalogue, typically - a dialog would interrupt a page opened for something else: a
// notice says what was added and links to the cart, and the report waits for the cart
// page, where it opens once.
function show_result(result) {
	if (on_cart_page()) {
		show_report(result)
		return
	}
	try {
		sessionStorage.setItem(REPORT_KEY, JSON.stringify(result))
	} catch (e) {
		// Only the details are lost: the notice below still says what was added.
	}
	show_notice(result)
}

function show_stashed_report() {
	if (!on_cart_page()) {
		return
	}
	let result = null
	try {
		result = JSON.parse(sessionStorage.getItem(REPORT_KEY) || "null")
		sessionStorage.removeItem(REPORT_KEY)
	} catch (e) {
		return
	}
	if (result && result.ok) {
		show_report(result)
	}
}

function on_cart_page() {
	return window.location.pathname.replace(/\/+$/, "") === CART_PAGE
}

// True when this page load may open a merge dialog: a guest cart is waiting to be merged
// (which ends in a report or a failure notice), the report is open, or a report stashed
// off the cart page waits for it. Kept for any one-time notice that must not open on top of
// the merge dialog; it can then wait for a later page. Synchronous state only, so the
// answer does not depend on which ready handler runs first.
function may_open_dialog() {
	if (ioi_partners.guest_cart.items().length || report_open) {
		return true
	}
	if (!on_cart_page()) {
		return false
	}
	try {
		return !!sessionStorage.getItem(REPORT_KEY)
	} catch (e) {
		return false
	}
}

function show_notice(result) {
	const added = result.merged.concat(result.added_automatically)
		.map(line => `+${line.qty} ${product_label(line)}`)
	let subtitle = added.slice(0, NOTICE_LINES).join(" · ")
	if (added.length > NOTICE_LINES) {
		subtitle += ` · ${__("{0} more", [added.length - NOTICE_LINES])}`
	}
	if (result.skipped.length) {
		const not_added = __("Not added: {0}", [result.skipped.length])
		subtitle = subtitle ? `${subtitle}<br>${not_added}` : not_added
	}
	// In the subtitle, not the toast's body: Frappe indents .alert-subtitle under the title
	// but leaves .alert-body unstyled, flush against the toast's left edge. Underlined
	// because a link there takes the text colour.
	const link = `<a href="${CART_PAGE}" style="text-decoration: underline">${__("See the details in your cart")}</a>`
	subtitle = subtitle ? `${subtitle}<br>${link}` : link

	frappe.show_alert({
		message: added.length
			? __("Your selection was added to your cart")
			: __("Your selection could not be added to your cart"),
		subtitle: subtitle,
		indicator: result.skipped.length ? "orange" : "green",
	}, NOTICE_SECONDS)
}

// What the merge changed, line by line: the units added to each product and what the cart
// now holds, the lines the cart rules added on their own, and the lines not added, each
// with the server's reason.
function show_report(result) {
	const added = result.merged.length + result.added_automatically.length > 0
	let body = `<p>${added
		? __("The products you selected before logging in are now in your cart.")
		: __("None of the products you selected before logging in could be added to your cart.")}</p>`

	body += report_section(__("Added to your cart"), result.merged, line => {
		let text = `${product_label(line)} <strong>+${line.qty}</strong> · ${__("{0} in your cart", [line.cart_qty])}`
		if (line.requested) {
			text += ` · ${__("{0} requested", [line.requested])}`
		}
		return text
	})
	body += report_section(
		__("Added automatically, required by a product you selected"),
		result.added_automatically,
		line => `${product_label(line)} <strong>+${line.qty}</strong> · ${__("{0} in your cart", [line.cart_qty])}`
	)
	body += report_section(__("Not added"), result.skipped, line => `${product_label(line)} (${line.qty})`)

	const d = new frappe.ui.Dialog({
		title: added ? __("Your selection was added to your cart") : __("Your selection could not be added to your cart"),
		primary_action_label: __("Close"),
		primary_action: () => d.hide(),
		onhide: () => {
			report_open = false
			d.$wrapper.remove()
		},
	})
	d.$body.append(body)
	report_open = true
	d.show()
}

function report_section(title, lines, render) {
	if (!lines.length) {
		return ""
	}
	const items = lines.map(line => {
		const note = line.message
			? `<div class="small text-muted">${frappe.utils.escape_html(line.message)}</div>`
			: ""
		return `<li>${render(line)}${note}</li>`
	}).join("")
	return `<h6>${title}</h6><ul style="padding-left: 20px">${items}</ul>`
}

function product_label(line) {
	return frappe.utils.escape_html(line.product_name || line.product_id)
}

ioi_partners.guest_cart_merge = {
	MERGED_EVENT,
	merge,
	may_open_dialog,
}

$(() => {
	show_stashed_report()
	merge()
})
